import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { CheckCircle, XCircle, Clock, FileText, User, Calendar, Utensils, MapPin } from 'lucide-react'

const API = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'

interface TimesheetLine {
  id: string
  date: string
  plannedStart?: string
  plannedEnd?: string
  validatedStart?: string
  validatedEnd?: string
  breakMinutes: number
  netHours: number
  isAbsence: boolean
  status: string
  correctionNote?: string
}

interface TimesheetData {
  id: string
  weekStart: string
  weekEnd: string
  status: string
  totalHours: number
  overtimeHours: number
  mealCount: number
  signatureStatus: string
  employeeSignedAt?: string
  note?: string
  lines: TimesheetLine[]
  employee: {
    firstName: string
    lastName: string
    establishment: { name: string }
  }
}

const LINE_STATUS: Record<string, { label: string; cls: string }> = {
  pending:   { label: 'En attente', cls: 'text-gray-500' },
  pointed:   { label: 'Badgé',      cls: 'text-blue-600' },
  corrected: { label: 'Corrigé',    cls: 'text-orange-600' },
  validated: { label: 'Validé',     cls: 'text-green-600' },
  absent:    { label: 'Absent',     cls: 'text-purple-600' },
}

function fmtDate(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}

function fmtHours(h: number) {
  const hh = Math.floor(h)
  const mm = Math.round((h - hh) * 60)
  if (mm === 0) return `${hh}h`
  return `${hh}h${String(mm).padStart(2, '0')}`
}

export default function TimesheetSign() {
  const { token } = useParams<{ token: string }>()
  const [ts, setTs] = useState<TimesheetData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [signNote, setSignNote] = useState('')
  const [signing, setSigning] = useState(false)
  const [signed, setSigned] = useState(false)

  useEffect(() => {
    if (!token) return
    axios.get(`${API}/timesheets/sign/${token}`)
      .then(r => { setTs(r.data); setLoading(false) })
      .catch(e => {
        setError(e.response?.data?.error || 'Lien invalide ou expiré')
        setLoading(false)
      })
  }, [token])

  async function handleSign() {
    if (!token) return
    setSigning(true)
    try {
      await axios.post(`${API}/timesheets/sign/${token}`, { note: signNote || undefined })
      setSigned(true)
      if (ts) setTs({ ...ts, signatureStatus: 'employee_signed', employeeSignedAt: new Date().toISOString() })
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur lors de la signature')
    } finally {
      setSigning(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500">Chargement de la feuille d'heures...</p>
        </div>
      </div>
    )
  }

  if (error && !ts) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Lien invalide</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    )
  }

  if (!ts) return null

  const alreadySigned = ts.signatureStatus === 'employee_signed' || ts.signatureStatus === 'validated'

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <FileText size={20} />
                <span className="font-semibold text-lg">Feuille d'heures</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <User size={15} className="text-gray-400" />
                <span className="font-medium">{ts.employee.firstName} {ts.employee.lastName}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Calendar size={14} />
                <span>Semaine du {fmtDate(ts.weekStart)} au {fmtDate(ts.weekEnd)}</span>
              </div>
              <div className="text-sm text-gray-400 mt-1">{ts.employee.establishment.name}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800">{fmtHours(ts.totalHours)}</div>
              <div className="text-xs text-gray-400">heures totales</div>
              {ts.overtimeHours > 0 && (
                <div className="text-sm text-orange-600 font-medium mt-1">+{fmtHours(ts.overtimeHours)} supp.</div>
              )}
              {ts.mealCount > 0 && (
                <div className="flex items-center gap-1 text-sm text-green-600 justify-end mt-1">
                  <Utensils size={13} />
                  <span>{ts.mealCount} repas</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Lines table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h2 className="font-semibold text-gray-700">Détail des heures</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="px-4 py-2 text-left">Jour</th>
                  <th className="px-4 py-2 text-center">Prévu</th>
                  <th className="px-4 py-2 text-center">Validé</th>
                  <th className="px-4 py-2 text-right">Net</th>
                  <th className="px-4 py-2 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {ts.lines.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400">Aucune ligne</td>
                  </tr>
                ) : ts.lines.map(line => {
                  const statusMeta = LINE_STATUS[line.status] || LINE_STATUS.pending
                  return (
                    <tr key={line.id} className={`border-t border-gray-50 ${line.isAbsence ? 'bg-purple-50' : ''}`}>
                      <td className="px-4 py-3 font-medium text-gray-700">{fmtDate(line.date)}</td>
                      <td className="px-4 py-3 text-center text-gray-500 font-mono text-xs">
                        {line.plannedStart && line.plannedEnd
                          ? `${line.plannedStart}–${line.plannedEnd}`
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center font-mono text-xs">
                        {line.isAbsence ? (
                          <span className="text-purple-600 font-medium">Absence</span>
                        ) : line.validatedStart && line.validatedEnd ? (
                          <span className={line.status === 'pointed' ? 'text-blue-600' : line.status === 'corrected' ? 'text-orange-600' : 'text-gray-700'}>
                            {line.validatedStart}–{line.validatedEnd}
                            {line.status === 'pointed' && (
                              <MapPin size={10} className="inline ml-1 text-blue-400" />
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">
                        {line.netHours > 0 ? fmtHours(line.netHours) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`text-xs font-medium ${statusMeta.cls}`}>{statusMeta.label}</span>
                        {line.correctionNote && (
                          <div className="text-xs text-gray-400 italic mt-0.5">{line.correctionNote}</div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Signature zone */}
        {signed || alreadySigned ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-green-800 mb-1">Feuille signée</h2>
            <p className="text-green-600 text-sm">
              {ts.employeeSignedAt
                ? `Signée le ${new Date(ts.employeeSignedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : 'Votre signature a bien été enregistrée.'}
            </p>
          </div>
        ) : ts.status !== 'validated' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
            <Clock className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h2 className="text-base font-semibold text-amber-800 mb-1">En attente de validation manager</h2>
            <p className="text-amber-600 text-sm">Vous pourrez signer cette feuille une fois qu'elle aura été validée par votre responsable.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-700 mb-1">Signer la feuille d'heures</h2>
            <p className="text-sm text-gray-500 mb-4">
              En signant, vous confirmez l'exactitude des heures indiquées pour la semaine du {fmtDate(ts.weekStart)} au {fmtDate(ts.weekEnd)}.
            </p>

            {error && (
              <div className="mb-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
            )}

            <textarea
              value={signNote}
              onChange={e => setSignNote(e.target.value)}
              placeholder="Commentaire (optionnel)..."
              rows={2}
              className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300 mb-4"
            />

            <button
              onClick={handleSign}
              disabled={signing}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {signing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Signer électroniquement
                </>
              )}
            </button>

            <p className="text-xs text-gray-400 text-center mt-3">
              Cette signature électronique est juridiquement valable. Votre adresse IP et l'heure seront enregistrées.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
